import { Link, useLocation, useNavigate } from 'react-router-dom';
import { LogOut, LayoutGrid, FlaskConical, ArrowLeft, Newspaper, Smartphone } from 'lucide-react';
import { cn } from '@/lib/utils';
import { DASHBOARD_MODULES } from '../config';
import { useDashboard } from '../context/DashboardContext';
import { useAuth } from '@/contexts/AuthContext';
import { useAccessibility } from '@/hooks/useAccessibility';
import { DashboardThemeToggle } from './DashboardThemeToggle';
import logoAzul from '@/assets/logo_azul.webp';
import logoBranca from '@/assets/logo_branca.png';

export type SidebarMode = 'home' | 'event' | 'analysis' | 'blog' | 'app';

interface DashboardSidebarProps {
  mode: SidebarMode;
  eventId?: string;
}

export function DashboardSidebar({ mode }: DashboardSidebarProps) {
  const { signOut } = useAuth();
  const { isDarkMode } = useAccessibility();
  const navigate = useNavigate();
  const location = useLocation();

  const handleSignOut = async () => {
    await signOut();
    navigate('/login', { replace: true });
  };

  const isEventsHome = location.pathname === '/dashboard' || location.pathname === '/dashboard/';
  const isAnalysis =
    location.pathname.startsWith('/dashboard/analysis');
  const isBlog = location.pathname.startsWith('/dashboard/blog');
  const isApp = location.pathname.startsWith('/dashboard/app');

  return (
    <aside className="flex flex-col shrink-0 w-60 h-dvh bg-secondary">
      <div className="shrink-0 h-16 px-4 flex items-center gap-2.5">
        <img
          src={isDarkMode ? logoBranca : logoAzul}
          alt="CONECTAR"
          className="h-8 w-auto shrink-0 object-contain"
        />
        <div className="min-w-0 leading-tight">
          {/* Wordmark na fonte da marca (Quicksand) — o resto do dashboard usa Inter (font-dash herdada). */}
          <p className="font-sans text-sm font-semibold text-foreground truncate">CONECTAR</p>
          <p className="text-[11px] text-muted-foreground truncate">Pesquisa</p>
        </div>
      </div>

      <nav className="flex-1 overflow-y-auto p-2">
        {mode === 'event' ? (
          <EventNav />
        ) : (
          <>
            <Link
              to="/dashboard"
              className={cn(
                'w-full flex items-center gap-2.5 px-3 py-2 rounded-[10px] text-sm transition-colors mb-0.5',
                isEventsHome && !isAnalysis && !isBlog && !isApp
                  ? 'bg-blue-100 dark:bg-background text-foreground font-medium shadow-[0_1px_2px_rgba(0,0,0,0.05)]'
                  : 'text-muted-foreground hover:bg-muted hover:text-foreground'
              )}
            >
              <LayoutGrid
                className={cn(
                  'h-4 w-4 shrink-0',
                  isEventsHome && !isAnalysis && !isBlog && !isApp ? 'text-primary' : 'text-muted-foreground'
                )}
              />
              Eventos de pesquisa
            </Link>
            <Link
              to="/dashboard/analysis"
              className={cn(
                'w-full flex items-center gap-2.5 px-3 py-2 rounded-[10px] text-sm transition-colors mb-0.5',
                isAnalysis
                  ? 'bg-blue-100 dark:bg-background text-foreground font-medium shadow-[0_1px_2px_rgba(0,0,0,0.05)]'
                  : 'text-muted-foreground hover:bg-muted hover:text-foreground'
              )}
            >
              <FlaskConical
                className={cn(
                  'h-4 w-4 shrink-0',
                  isAnalysis ? 'text-primary' : 'text-muted-foreground'
                )}
              />
              Análise de eventos
            </Link>
            <Link
              to="/dashboard/blog"
              className={cn(
                'w-full flex items-center gap-2.5 px-3 py-2 rounded-[10px] text-sm transition-colors mb-0.5',
                isBlog
                  ? 'bg-blue-100 dark:bg-background text-foreground font-medium shadow-[0_1px_2px_rgba(0,0,0,0.05)]'
                  : 'text-muted-foreground hover:bg-muted hover:text-foreground'
              )}
            >
              <Newspaper
                className={cn(
                  'h-4 w-4 shrink-0',
                  isBlog ? 'text-primary' : 'text-muted-foreground'
                )}
              />
              Blog
            </Link>
            <Link
              to="/dashboard/app"
              className={cn(
                'w-full flex items-center gap-2.5 px-3 py-2 rounded-[10px] text-sm transition-colors mb-0.5',
                isApp
                  ? 'bg-blue-100 dark:bg-background text-foreground font-medium shadow-[0_1px_2px_rgba(0,0,0,0.05)]'
                  : 'text-muted-foreground hover:bg-muted hover:text-foreground'
              )}
            >
              <Smartphone
                className={cn(
                  'h-4 w-4 shrink-0',
                  isApp ? 'text-primary' : 'text-muted-foreground'
                )}
              />
              Aplicativo
            </Link>
          </>
        )}
      </nav>

      <div className="shrink-0 mt-auto p-2 space-y-0.5">
        <DashboardThemeToggle showLabel className="w-full justify-start px-3 py-2" />
        <button
          type="button"
          onClick={handleSignOut}
          className="w-full flex items-center gap-2 px-3 py-2 rounded-[10px] text-xs text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors"
        >
          <LogOut className="h-4 w-4" />
          Sair
        </button>
      </div>
    </aside>
  );
}

function EventNav() {
  const { activeModule, setActiveModule } = useDashboard();

  return (
    <>
      <Link
        to="/dashboard"
        className="w-full flex items-center gap-2 px-3 py-2 rounded-md text-xs text-muted-foreground hover:text-foreground hover:bg-muted mb-2 transition-colors"
      >
        <ArrowLeft className="h-3.5 w-3.5" />
        Voltar aos eventos
      </Link>
      {DASHBOARD_MODULES.map((mod) => {
        const Icon = mod.icon;
        const active = activeModule === mod.id;
        return (
          <button
            key={mod.id}
            type="button"
            onClick={() => setActiveModule(mod.id)}
            className={cn(
              'w-full flex items-center gap-2.5 px-3 py-2 rounded-md text-sm transition-colors mb-0.5',
              active
                ? 'bg-blue-100 dark:bg-background text-foreground font-medium shadow-sm'
                : 'text-muted-foreground hover:bg-muted hover:text-foreground'
            )}
          >
            <Icon
              className={cn(
                'h-4 w-4 shrink-0',
                active ? 'text-primary' : 'text-muted-foreground'
              )}
            />
            <span className="truncate">{mod.label}</span>
          </button>
        );
      })}
    </>
  );
}
